import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import { endOfDay } from 'date-fns/endOfDay';
import { max } from 'date-fns/max';
import { min } from 'date-fns/min';
import { startOfDay } from 'date-fns/startOfDay';

import { minutesBetween } from '../core/calendar-time-math.js';

/**
 * Pure positioning math for the calendar time grid.
 *
 * These functions translate an occurrence's absolute start/end into the
 * minute-offsets and day-span the renderer needs. They hold no Lit, no DOM and
 * no pixel constants (the px-per-minute scale lives in CSS) — only the temporal
 * geometry, so they stay reusable across the grid redesign.
 */

const MINUTES_PER_DAY = 24 * 60;

/** Minimum visible vertical extent for a timed event, in minutes. */
const MIN_TIMED_EVENT_MINUTES = 30;

export interface TimedEventLayoutInput {
  endAt: Date;
  id: string;
  startAt: Date;
}

export interface TimedEventGeometry {
  endMinute: number;
  heightMinute: number;
  startMinute: number;
}

export interface TimedEventLayout {
  column: number;
  columns: number;
}

/**
 * Vertical placement of a timed occurrence within a single day column,
 * clamped to the day's bounds with a minimum height floor.
 */
export function timedEventGeometry(
  day: Date,
  startAt: Date,
  endAt: Date
): TimedEventGeometry {
  const dayStart = startOfDay(day);
  const startMinute = Math.max(
    0,
    minutesBetween(dayStart, max([startAt, dayStart]))
  );
  const endMinute = Math.min(
    MINUTES_PER_DAY,
    minutesBetween(dayStart, min([endAt, endOfDay(day)]))
  );
  const heightMinute = Math.max(
    MIN_TIMED_EVENT_MINUTES,
    endMinute - startMinute
  );
  return { endMinute, heightMinute, startMinute };
}

/**
 * Assigns overlapping timed events into stable visual lanes.
 *
 * Events that overlap in time share a cluster. Within a cluster each event gets
 * the first free column; all events in the cluster receive the final cluster
 * width, so rendered columns line up instead of stacking on top of each other.
 */
export function layoutTimedEvents(
  events: readonly TimedEventLayoutInput[]
): Map<string, TimedEventLayout> {
  const sorted = [...events].sort((left, right) => {
    const startDelta = left.startAt.getTime() - right.startAt.getTime();
    if (startDelta !== 0) return startDelta;
    const durationDelta =
      right.endAt.getTime() -
      right.startAt.getTime() -
      (left.endAt.getTime() - left.startAt.getTime());
    if (durationDelta !== 0) return durationDelta;
    return left.id.localeCompare(right.id);
  });

  const result = new Map<string, TimedEventLayout>();
  let cluster: TimedEventLayoutInput[] = [];
  let clusterEnd = Number.NEGATIVE_INFINITY;

  const flushCluster = () => {
    if (!cluster.length) return;
    const columnEnds: number[] = [];
    const assignments = new Map<string, number>();

    for (const event of cluster) {
      const start = event.startAt.getTime();
      const column = columnEnds.findIndex(end => end <= start);
      const targetColumn = column >= 0 ? column : columnEnds.length;
      columnEnds[targetColumn] = event.endAt.getTime();
      assignments.set(event.id, targetColumn);
    }

    const columns = Math.max(1, columnEnds.length);
    for (const event of cluster) {
      result.set(event.id, {
        column: assignments.get(event.id) ?? 0,
        columns,
      });
    }

    cluster = [];
    clusterEnd = Number.NEGATIVE_INFINITY;
  };

  for (const event of sorted) {
    const start = event.startAt.getTime();
    const end = Math.max(start, event.endAt.getTime());
    if (cluster.length && start >= clusterEnd) {
      flushCluster();
    }
    cluster.push(event);
    clusterEnd = Math.max(clusterEnd, end);
  }
  flushCluster();

  return result;
}

/**
 * CSS `grid-column` span for an all-day occurrence across a visible day range,
 * clamped to the first/last visible day.
 */
export function allDayGridColumn(
  startAt: Date,
  endAt: Date,
  days: readonly Date[]
): string {
  const first = days[0];
  if (!first) return '';
  const firstDay = startOfDay(first);
  const startIndex = Math.max(
    0,
    differenceInCalendarDays(startOfDay(startAt), firstDay)
  );
  const endIndex = Math.min(
    days.length - 1,
    differenceInCalendarDays(startOfDay(endAt), firstDay)
  );
  return `grid-column:${startIndex + 1}/${endIndex + 2}`;
}
